import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { verifyEmail } from '../services';
import { useUserStore } from '../stores';
import { trackEvent } from '../hooks/useAnalytics';
import { markAuthHandoff } from '../utils/authHandoff';
import './Login.css';

const VerifyEmail: React.FC = () => {
  const { uid, token } = useParams<{ uid: string; token: string }>();
  // 'expired': the server refused the link itself (a 400 — expired, used, or
  // malformed); 'error' is everything else (network, 5xx, a missing param).
  const [status, setStatus] = useState<'verifying' | 'success' | 'expired' | 'error'>('verifying');
  const [error, setError] = useState('');

  useEffect(() => {
    const verify = async () => {
      if (!uid || !token) {
        setStatus('error');
        setError('Invalid verification link');
        return;
      }

      try {
        await verifyEmail(uid, token);
        // Funnel step: plan → signup (email path; OAuth path tracks via ?welcome=1)
        trackEvent('signup_completed', { method: 'email' });
        setStatus('success');
        // The server logged this browser in (`verify_email` calls `login`),
        // but only a full-page load told the store so — and the OAuth path
        // gets one from its redirect while this path never did. Refreshing
        // here is what lets a fork begun before signup resume; the store
        // redirects if an intent is waiting, and otherwise just knows the
        // session.
        markAuthHandoff();
        await useUserStore.getState().fetchUser();
      } catch (err: unknown) {
        // Duck-typed rather than `instanceof ApiError`, so a caller or test
        // that supplies only `verifyEmail` still reaches the generic branch.
        if ((err as { status?: unknown } | null)?.status === 400) {
          setStatus('expired');
          return;
        }
        setStatus('error');
        setError(err instanceof Error ? err.message : 'Verification failed');
      }
    };

    verify();
  }, [uid, token]);

  return (
    <div className="login-page">
      <div className="login-container">
        {status === 'verifying' && (
          <>
            <h1 className="login-title">Email Verification</h1>
            <div className="login-subtitle">
              Verifying your email…
            </div>
          </>
        )}

        {status === 'success' && (
          <>
            <h1 className="login-title">Email confirmed</h1>
            <p className="login-body">
              Your email has been verified successfully. Your plans are waiting.
            </p>
            <a href="vizday://login" className="login-button email-login" style={{ textDecoration: 'none' }}>
              Open VizDay App
            </a>
            <p className="login-fine">
              If the app does not open, return to VizDay and tap Sign In.
            </p>
            <Link to="/login" className="login-button" style={{ textDecoration: 'none', marginTop: 'var(--space-lg)' }}>
              Continue on the Web
            </Link>
          </>
        )}

        {status === 'expired' && (
          <>
            <h1 className="login-title">This link has run out</h1>
            <div className="login-err" role="alert">
              <span className="login-err-mark" aria-hidden="true">!</span>
              <p>
                <strong>It expired, or it was already used.</strong> If you already confirmed, just sign in.
              </p>
            </div>
            <Link to="/login" className="login-button email-login" style={{ textDecoration: 'none' }}>
              Sign in
            </Link>
          </>
        )}

        {status === 'error' && (
          <>
            <h1 className="login-title">Email Verification</h1>
            <div className="login-error">{error}</div>
            <p className="login-subtitle">
              The verification link may have expired or is invalid.
            </p>
            <Link to="/login" className="login-button email-login" style={{ textDecoration: 'none' }}>
              Back to Login
            </Link>
          </>
        )}
      </div>
    </div>
  );
};

export default VerifyEmail;
